import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Text } from "@nbs/ui";
import { formatNbsNumber } from "@/features/analytics/utils/format-number";
import { CHART_ANIMATION_DURATION_MS } from "./chartAnimation";

export type KpiStatusTone = "neutral" | "success" | "warning" | "danger";

/** Decorative glyphs, keyed by a stable name (nature / analytics themed). */
const KPI_ICONS: Record<string, string> = {
  leaf: "M12 4c3 2 5 4.5 5 8a5 5 0 0 1-10 0c0-3.5 2-6 5-8Z",
  measure: "M4 12h4l3 7 4-14 3 7h2",
  indicator: "M4 6h16M4 12h16M4 18h10",
  territory: "M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z",
  gauge: "M4 13a8 8 0 1 1 16 0M12 13l3-2",
  crop: "M12 21V9m0 4c-3 0-5-2-5-5 3 0 5 2 5 5Zm0 0c3 0 5-2 5-5-3 0-5 2-5 5Z",
  forest: "M12 3 7 11h3l-4 7h12l-4-7h3L12 3Z",
  coin: "M12 3v18M8 7h6a2 2 0 0 1 0 4H9a2 2 0 0 0 0 4h7",
  erosion: "M3 12c3-3 6 3 9 0s6-3 9 0M3 18c3-3 6 3 9 0s6-3 9 0",
  alert: "M12 3 2 20h20L12 3ZM12 10v4m0 3h.01",
};

type Point = readonly [number, number];

/** Catmull-Rom → cubic Bézier path (smooth sparkline, demo-like). */
function smoothLinePath(points: readonly Point[]): string {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M ${points[0]![0]} ${points[0]![1]} L ${points[1]![0]} ${points[1]![1]}`;
  }
  let d = `M ${points[0]![0]} ${points[0]![1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]!;
    const p1 = points[i]!;
    const p2 = points[i + 1]!;
    const p3 = points[i + 2] ?? p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

function Sparkline({ values, tone }: { values: readonly number[]; tone: "up" | "down" | "flat" }) {
  const pts = values.filter((v) => Number.isFinite(v));
  if (pts.length < 2) return null;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const span = max - min || 1;
  const w = 200;
  const h = 48;
  // Keep stroke inside the viewBox (stroke is centered on the path).
  const padX = 3;
  const padY = 4;
  const step = (w - padX * 2) / (pts.length - 1);
  const coords: Point[] = pts.map((v, i) => {
    const x = padX + i * step;
    const y = h - padY - ((v - min) / span) * (h - padY * 2);
    return [x, y];
  });
  const line = smoothLinePath(coords);
  const first = coords[0]!;
  const last = coords[coords.length - 1]!;
  const area = `${line} L ${last[0]} ${h - 1} L ${first[0]} ${h - 1} Z`;
  // Remount when series changes so the entrance draw replays (like chart mountKey).
  const animKey = pts.map((v) => String(v)).join(",");

  const stroke =
    tone === "up"
      ? "var(--nbs-color-primary)"
      : tone === "down"
        ? "var(--nbs-color-danger)"
        : "var(--nbs-color-primary)";
  const fill = tone === "down" ? "var(--nbs-color-danger)" : "var(--nbs-color-primary)";

  return (
    <svg
      key={animKey}
      className="nbs-kpi-card__spark nbs-kpi-card__spark--animate"
      width="100%"
      height="48"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ ["--nbs-spark-duration" as string]: `${CHART_ANIMATION_DURATION_MS}ms` }}
    >
      <path className="nbs-kpi-card__spark-area" d={area} fill={fill} stroke="none" />
      <path
        className="nbs-kpi-card__spark-line"
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        pathLength={1}
      />
    </svg>
  );
}

export type NbsKpiCardProps = {
  readonly label: string;
  readonly value: string | number | null | undefined;
  readonly unit?: string | null;
  readonly period?: string | null;
  readonly territory?: string | null;
  readonly trend?: string | null;
  readonly source?: string | null;
  readonly n2Href?: string | null;
  readonly icon?: keyof typeof KPI_ICONS;
  /** Signed rate shown as a coloured chip (e.g. +36.5). */
  readonly delta?: number | null;
  readonly deltaSuffix?: string;
  readonly deltaLabel?: string | null;
  /** Real per-interval series for the mini area chart. */
  readonly spark?: readonly number[] | null;
  readonly status?: string | null;
  readonly statusTone?: KpiStatusTone;
  readonly valueTone?: "up" | "down" | null;
};

export function NbsKpiCard({
  label,
  value,
  unit,
  period,
  territory,
  n2Href,
  icon,
  delta,
  deltaSuffix = "%",
  deltaLabel,
  spark,
  status,
  statusTone = "neutral",
  valueTone,
}: NbsKpiCardProps) {
  const display =
    value == null || value === ""
      ? "—"
      : typeof value === "number"
        ? formatNbsNumber(value)
        : value;
  const iconPath = icon ? KPI_ICONS[icon] : null;
  const deltaTone: "up" | "down" | "flat" =
    delta == null
      ? valueTone === "up" || valueTone === "down"
        ? valueTone
        : "flat"
      : delta > 0
        ? "up"
        : delta < 0
          ? "down"
          : "flat";
  const isStable = delta != null && delta === 0;
  const footLabel = isStable ? "stable" : deltaLabel;
  const meta = [period, territory].filter(Boolean).join(" · ");
  const valueClass = valueTone
    ? `nbs-kpi-card__value nbs-kpi-card__value--${valueTone}`
    : "nbs-kpi-card__value";
  const hasSpark = Boolean(spark && spark.length >= 2);
  const className = [
    "nbs-kpi-card",
    hasSpark ? "nbs-kpi-card--spark" : "",
    n2Href ? "nbs-kpi-card--link" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content: ReactNode = (
    <>
      <div className="nbs-kpi-card__body">
        <div className="nbs-kpi-card__top">
          <Text tone="muted" className="nbs-kpi-card__label">
            {label}
          </Text>
          {iconPath ? (
            <span className="nbs-kpi-card__icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={iconPath} />
              </svg>
            </span>
          ) : null}
        </div>

        <p className={valueClass}>
          {display}
          {unit && display !== "—" ? <span className="nbs-kpi-card__unit"> {unit}</span> : null}
        </p>

        {delta != null || status || footLabel ? (
          <div className="nbs-kpi-card__foot">
            {delta != null ? (
              <span className={`nbs-kpi-card__delta nbs-kpi-card__delta--${deltaTone}`}>
                {deltaTone === "up" ? "▲" : deltaTone === "down" ? "▼" : "—"}{" "}
                {formatNbsNumber(Math.abs(delta))}
                {isStable ? "" : deltaSuffix}
              </span>
            ) : null}
            {status && !isStable ? (
              <span className={`nbs-kpi-card__status nbs-kpi-card__status--${statusTone}`}>
                {status}
              </span>
            ) : null}
            {footLabel ? <span className="nbs-kpi-card__delta-label">{footLabel}</span> : null}
          </div>
        ) : null}

        {meta ? (
          <Text tone="muted" className="nbs-kpi-card__meta">
            {meta}
          </Text>
        ) : null}
      </div>

      {hasSpark ? <Sparkline values={spark!} tone={deltaTone} /> : null}
    </>
  );

  if (n2Href) {
    return (
      <Link
        to={n2Href}
        className={`${className} nbs-kpi-card--has-tip`}
        aria-label={`Voir l’indicateur : ${label}`}
      >
        {content}
        <span className="nbs-kpi-card__tooltip" role="tooltip">
          Voir l’indicateur
        </span>
      </Link>
    );
  }

  return <article className={className}>{content}</article>;
}
